import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Component, computed, effect, HostListener, inject, OnDestroy, OnInit, ViewEncapsulation, DestroyRef } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { PRIMARY_OUTLET, Router, RouterOutlet } from "@angular/router";
import type { HubConnection } from "@microsoft/signalr";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import { Subscription, take } from "rxjs";
import { SignalRService } from "./real-time-notifications/signalr-service/signalr.service";
import { AuthService, BroadcastService, CardViewService, HelpersService, UsersService } from "./shared";
import { IframeMessage, IframeMessagesService } from "./shared/iframe-messages-service/iframe-messages.service";
import { JumpstartComponentsWkIconsModule } from "@wk/components-v3-angular17";
import { WkIconUser2SpritePds3 } from "./shared/icons/wk-icon-user-sprite-pds3/index";
import { AppLoaderComponent } from "./shared/components/app-loader/app-loader.component";
import { NotificationComponent } from "./notification/notification.component";
import { LanguageService } from "@services/language.service";

/**
 * App module TS
 */
@Component({
    selector: "wk-app-root",
    templateUrl: "./app.component.html",
    styleUrl: "./app.component.scss",
    encapsulation: ViewEncapsulation.None,
    imports: [JumpstartComponentsWkIconsModule, WkIconUser2SpritePds3, RouterOutlet, AppLoaderComponent, NotificationComponent, TranslatePipe],
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly destroyRef = inject(DestroyRef);
    private readonly authService = inject(AuthService);
    private readonly cardViewService = inject(CardViewService);
    private readonly helpersService = inject(HelpersService);
    private readonly languageService = inject(LanguageService);
    private readonly router = inject(Router);
    private readonly signalRService = inject(SignalRService);
    private readonly titleService = inject(Title);
    private readonly translateService = inject(TranslateService);
    readonly usersService = inject(UsersService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private stringBroadcastSubscription = new Subscription();
    protected readonly isAccessErrorRoute = computed(() => {
        const url = this.router.lastSuccessfulNavigation()?.finalUrl;
        const route = url?.root.children[PRIMARY_OUTLET]?.segments[0]?.path;
        return route === "accessdenied" || route === "accessunauthorized" || route === "unauthorizedroute";
    });

    private signalRConnection: HubConnection | null = null;
    showNavigationDenied = false;
    showNavigationUnauthorized = false;
    sessionInitialized = false;
    isFromSettingsPage = false;

    constructor() {
        // Hide the loader text if the iframe url is from settings page
        // Not using the Router url is initially set to "/" in the appComponent constructor and ngOnInit because navigation has not completed.
        // the correct url, available as urlAfterRedirects, is only available after the Router NavigationEnd event is completed.
        // this causes the loader text to briefly appear until navigation finishes.
        // Using window.location.href to determine if we are in the settings page as a workaround.
        this.isFromSettingsPage = window?.self?.location?.href?.includes("/settings/");

        this.translateService.setFallbackLang(this.languageService.getLang());
        this.registerStringBroadcast();
        effect(() => {
            if (this.signalRService.signalRConnected()) {
                this.listenToSignalRMessages();
            } else {
                this.stopListeningToSignalRMessages();
            }
        });
        this.helpersService.unauthorizedNavigationEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.showNavigationDenied = true;
        });
        this.helpersService.noUdsNavigationEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.showNavigationUnauthorized = true;
        });
        effect(() => {
            const session = this.authService.sessionData();
            this.sessionInitialized = session?.sessionName != null;
        });
    }

    ngOnInit() {
        this.translateService
            .get("General.Title")
            .pipe(take(1))
            .subscribe((string) => {
                this.titleService.setTitle(string);
            });
        this.helpersService.suppressSpinnerEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.sessionInitialized = true;
        });
    }

    listenToSignalRMessages(): boolean {
        const connection = this.signalRService.connection;
        if (!connection || this.signalRConnection === connection) {
            return false;
        }
        this.stopListeningToSignalRMessages();
        this.signalRConnection = connection;

        connection.on("user-updated", async (userId, userData) => {
            if (typeof userData === "string") {
                userData = JSON.parse(userData);
            }

            if (userData) {
                this.usersService.userChangedSubject.next({
                    userId: userId,
                    userData,
                });
            }

            const user = this.usersService.getUserById(userId);
            if (user && user.Active !== userData.Active) {
                user.Active = userData.Active;
                this.helpersService.refreshFilters();
            }
        });

        connection.on("dashboard-updated", () => {
            this.cardViewService.setCurrentViewType(this.cardViewService.currentViewType);
            this.cardViewService.refreshCardsViewLabelsSubject.next(false);
        });
    }

    stopListeningToSignalRMessages(): boolean {
        const connection = this.signalRConnection;
        if (!connection) {
            return false;
        }
        this.signalRConnection = null;
        if (connection) {
            connection.off("user-updated");
            connection.off("dashboard-updated");
        }
    }

    ngOnDestroy() {
        this.stopListeningToSignalRMessages();
        this.unregisterStringBroadcast();
    }

    registerStringBroadcast() {
        this.stringBroadcastSubscription.add(
            BroadcastService.on<string>("navigate-to").subscribe((url) => {
                this.router.navigateByUrl(url);
            }),
        );

        this.stringBroadcastSubscription.add(
            BroadcastService.on<IframeMessage>("setLanguage").subscribe((message) => {
                this.iframeMessagesService.postMessage("languageInitialized", null);
                // Also switches ngx-translate, so an unsupported host language no longer desyncs the two.
                this.languageService.setLang(message.data.language);
                const lang = this.languageService.getLang();
                this.translateService
                    .get("General.Title")
                    .pipe(take(1))
                    .subscribe((string) => {
                        this.titleService.setTitle(string);
                    });
                BroadcastService.broadcast("lang-change", lang);
            }),
        );
    }

    unregisterStringBroadcast() {
        this.stringBroadcastSubscription.unsubscribe();
        this.stringBroadcastSubscription = new Subscription();
    }

    @HostListener("document:keydown.escape") onKeydownHandler() {
        this.showNavigationDenied = false;
    }
}
